import React, { useState, useEffect } from 'react';
import { 
  Bot, 
  Sparkles, 
  Save, 
  RotateCw, 
  Plus, 
  Trash2, 
  Star, 
  Clock, 
  MessageSquare, 
  ShieldCheck, 
  CheckCircle2, 
  AlertCircle, 
  ExternalLink,
  Search,
  Filter,
  Eye,
  X,
  Sliders,
  Award,
  Zap,
  Check,
  Smartphone,
  Send,
  ShoppingBag,
  RefreshCw
} from 'lucide-react';
import { ChatAndShopConfig, ChatAndShopFewShotExample, ChatAndShopTicket } from '../types';
import { ChatAndShopView } from './ChatAndShopView';

interface ChatAndShopSettingsTabProps {
  onOpenLiveChat?: () => void;
}

export const ChatAndShopSettingsTab: React.FC<ChatAndShopSettingsTabProps> = ({
  onOpenLiveChat
}) => {
  const [subTab, setSubTab] = useState<'config' | 'tickets' | 'simulator'>('config');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // AI Configuration State
  const [config, setConfig] = useState<ChatAndShopConfig>({
    role: 'คุณคือ AI Agent ชื่อ "PINKGIFF" (พิงค์กิฟ) ที่ปรึกษาการขายและผู้ช่วยช้อปส่วนตัวประจำร้านค้าออนไลน์กิฟฟารีน ของแท้ 100% ส่งตรงจากศูนย์บริการ',
    modelVersion: 'gemini-3.8-flash',
    toneOfVoice: 'อบอุ่น เป็นกันเอง สุภาพ มีชีวิตชีวา พูดคุยสไตล์เพื่อนแนะนำเพื่อน ไพเราะน่ารัก (เช่น ค่ะ, นะคะ, น้า ❤️) อธิบายสรรพคุณสินค้าเข้าใจง่าย ชัดเจนเรื่องราคา สิทธิประโยชน์สมาชิก และคะแนนสะสม PV',
    temperature: 0.7,
    autoCloseTimeoutMinutes: 3,
    fewShotExamples: []
  });

  // Tickets & CSAT Quality Audit State
  const [tickets, setTickets] = useState<ChatAndShopTicket[]>([]);
  const [ticketsSummary, setTicketsSummary] = useState<any>(null);
  const [ticketSearch, setTicketSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState<'all' | '5' | '4' | 'low' | 'timeout'>('all');
  const [selectedTicketForTranscript, setSelectedTicketForTranscript] = useState<ChatAndShopTicket | null>(null);

  // Simulator Test State & Live Cart
  const [simCart, setSimCart] = useState<any[]>([]);
  const [simCartCount, setSimCartCount] = useState(0);
  const [simCartTotal, setSimCartTotal] = useState(0);
  const [simCustomer, setSimCustomer] = useState<{
    id: string;
    name: string;
    memberId: string;
    phone: string;
    address: string;
    isMember: boolean;
  }>({
    id: 'SIM-USER-108',
    name: 'คุณสมชาย ใจดี',
    memberId: '108037654',
    phone: '081-234-5678',
    address: '88/12 หมู่ 5 ต.บางรักพัฒนา อ.บางบัวทอง จ.นนทบุรี 11110',
    isMember: true
  });

  const handleSimAddToCart = (product: any, qty = 1) => {
    setSimCart(prev => {
      const idx = prev.findIndex(item => item.id === product.id || item.code === product.code);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + qty };
        return updated;
      }
      return [...prev, {
        id: product.id || product.code,
        code: product.code,
        name: product.name,
        price: Number(product.memberPrice || product.price),
        quantity: qty,
        image: product.image,
        pv: Number(product.pv || 0)
      }];
    });
  };

  useEffect(() => {
    const totalCount = simCart.reduce((sum, item) => sum + (item.quantity || 1), 0);
    const totalPrice = simCart.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
    setSimCartCount(totalCount);
    setSimCartTotal(totalPrice);
  }, [simCart]);

  // Load Data
  const fetchData = async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      // 1. Fetch Config
      const configRes = await fetch('/api/chatandshop/config');
      if (configRes.ok) {
        const data = await configRes.json();
        if (data.config) {
          setConfig(data.config);
        }
      }

      // 2. Fetch Tickets
      const ticketsRes = await fetch('/api/chatandshop/tickets');
      if (ticketsRes.ok) {
        const tData = await ticketsRes.json();
        setTickets(tData.tickets || []);
        setTicketsSummary(tData.summary || null);
      }
    } catch (err: any) {
      console.error('Fetch chat and shop data error:', err);
      setErrorMessage('ไม่สามารถโหลดข้อมูลระบบ Chat & Shop ได้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Save Configuration
  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      setErrorMessage('');
      setSaveSuccess(false);

      const res = await fetch('/api/chatandshop/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      if (res.ok) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 4000);
      } else {
        const d = await res.json();
        setErrorMessage(d.error || 'บันทึกการตั้งค่าไม่สำเร็จ');
      }
    } catch (err: any) {
      console.error('Save config error:', err);
      setErrorMessage('เกิดข้อผิดพลาดในการบันทึกการตั้งค่า');
    } finally {
      setSaving(false);
    }
  };

  // Add Few Shot Example
  const handleAddExample = () => {
    const newEx: ChatAndShopFewShotExample = {
      id: `ex-${Date.now()}`,
      question: '',
      answer: '',
      toneNotes: ''
    };
    setConfig(prev => ({
      ...prev,
      fewShotExamples: [...(prev.fewShotExamples || []), newEx]
    }));
  };

  // Update Few Shot Example
  const handleUpdateExample = (id: string, field: keyof ChatAndShopFewShotExample, value: string) => {
    setConfig(prev => ({
      ...prev,
      fewShotExamples: (prev.fewShotExamples || []).map(ex => 
        ex.id === id ? { ...ex, [field]: value } : ex
      )
    }));
  };

  // Delete Few Shot Example
  const handleDeleteExample = (id: string) => {
    setConfig(prev => ({
      ...prev,
      fewShotExamples: (prev.fewShotExamples || []).map(ex => ex).filter(ex => ex.id !== id)
    }));
  };

  // Filtered Tickets
  const filteredTickets = tickets.filter(t => {
    const matchSearch = !ticketSearch || 
      t.ticketId.toLowerCase().includes(ticketSearch.toLowerCase()) ||
      (t.customerName && t.customerName.toLowerCase().includes(ticketSearch.toLowerCase())) ||
      (t.phone && t.phone.includes(ticketSearch)) ||
      (t.memberId && t.memberId.includes(ticketSearch));

    if (!matchSearch) return false;

    if (ratingFilter === 'all') return true;
    if (ratingFilter === '5') return t.rating === 5;
    if (ratingFilter === '4') return t.rating === 4;
    if (ratingFilter === 'low') return t.rating && t.rating <= 3;
    if (ratingFilter === 'timeout') return t.status === 'timeout';
    return true;
  });

  if (loading) {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-[360px] text-slate-500">
        <RotateCw className="w-8 h-8 text-teal-600 animate-spin mb-3" />
        <p className="text-sm font-medium">กำลังโหลดการตั้งค่า Chat & Shop (Gemini Agent)...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 1. Header Banner & Sub-Tabs */}
      <div className="bg-gradient-to-r from-slate-900 via-zinc-900 to-rose-950 p-5 rounded-2xl text-white shadow-md border border-zinc-800">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-xl bg-rose-500/20 border border-rose-400/40 flex items-center justify-center text-rose-300 shadow-inner">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-extrabold text-white tracking-tight">
                  Chat & Shop | ศูนย์ควบคุมและฝึกฝน Gemini AI
                </h2>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-500/20 text-teal-300 border border-teal-500/30">
                  v3.8 Flash Ready
                </span>
              </div>
              <p className="text-xs text-zinc-300 mt-0.5">
                กำหนดบทบาท, เวอร์ชันโมเดล, น้ำเสียงตัวอย่าง (Few-Shot) และตรวจสอบ Ticket คุณภาพการบริการ (1-5 ดาว)
              </p>
            </div>
          </div>

          {/* Sub Tab Switcher */}
          <div className="flex items-center bg-zinc-800/80 p-1 rounded-xl border border-zinc-700/80 self-start md:self-auto flex-wrap gap-1">
            <button
              onClick={() => setSubTab('config')}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                subTab === 'config'
                  ? 'bg-rose-600 text-white shadow-sm'
                  : 'text-zinc-300 hover:text-white'
              }`}
            >
              <Sliders className="w-3.5 h-3.5" />
              <span>ตั้งค่าบทบาท & เทรนโมเดล AI</span>
            </button>
            <button
              onClick={() => setSubTab('tickets')}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                subTab === 'tickets'
                  ? 'bg-rose-600 text-white shadow-sm'
                  : 'text-zinc-300 hover:text-white'
              }`}
            >
              <Star className="w-3.5 h-3.5 text-amber-300" />
              <span>Ticket & ประเมินคุณภาพ ({tickets.length})</span>
            </button>
            <button
              onClick={() => setSubTab('simulator')}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                subTab === 'simulator'
                  ? 'bg-rose-600 text-white shadow-sm'
                  : 'text-zinc-300 hover:text-white'
              }`}
            >
              <Smartphone className="w-3.5 h-3.5 text-teal-300" />
              <span>Chat & Shop เสมือน</span>
            </button>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {saveSuccess && (
        <div className="bg-emerald-50 border border-emerald-300 text-emerald-800 px-4 py-3 rounded-xl flex items-center gap-3 text-xs font-medium shadow-sm animate-fade-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <span>บันทึกการตั้งค่าบทบาท, โมเดล และตัวอย่าง Few-Shot ของ เจมิไน เรียบร้อยแล้ว! ระบบ AI จะนำไปใช้ในการประมวลผลรอบถัดไปทันที</span>
        </div>
      )}

      {errorMessage && (
        <div className="bg-rose-50 border border-rose-300 text-rose-800 px-4 py-3 rounded-xl flex items-center gap-3 text-xs font-medium shadow-sm">
          <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* SUB-TAB 1: CONFIGURATION & TRAINING */}
      {subTab === 'config' && (
        <div className="space-y-6">
          {/* Section 1: Core Model & Persona Parameters */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2 text-slate-800 font-bold text-sm">
                <Sparkles className="w-4 h-4 text-rose-600" />
                <span>1. กำหนดเวอร์ชันและพารามิเตอร์ของโมเดล เจมิไน (Gemini Model Configuration)</span>
              </div>
              <span className="text-[11px] text-slate-400">ประมวลผลแบบ Server-Side ด้วย @google/genai SDK</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Model Selector */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  เลือกเวอร์ชันโมเดล Gemini (Model Version)
                </label>
                <select
                  value={config.modelVersion}
                  onChange={(e) => setConfig({ ...config, modelVersion: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                >
                  <option value="gemini-3.8-flash">🌟 Gemini 3.8 Flash (แนะนำ: เสถียร ตอบไว แม่นยำเรื่องสินค้า)</option>
                  <option value="gemini-flash-latest">⚡ Gemini Flash 3.5 / Latest (ความเร็วสูงพิเศษ)</option>
                  <option value="gemini-3.1-flash-lite">🌱 Gemini 3.1 Flash Lite (ประหยัด Resource)</option>
                  <option value="gemini-3.1-pro-preview">🧠 Gemini 3.1 Pro Preview (ตรรกะเชิงลึก)</option>
                </select>
                <p className="text-[11px] text-slate-500 mt-1">
                  ปัจจุบันระบบเชื่อมต่อกับ Gemini Flash ที่มีความเร็วสูง เหมาะสำหรับการแชตและสรุปออเดอร์แบบเรียลไทม์
                </p>
              </div>

              {/* Auto Close Timeout */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center justify-between">
                  <span>ระยะเวลาจบการสนทนาอัตโนมัติ (Auto-close Inactivity Timer)</span>
                  <span className="text-rose-600 font-bold font-mono">{config.autoCloseTimeoutMinutes} นาที</span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="1"
                    max="15"
                    step="1"
                    value={config.autoCloseTimeoutMinutes || 3}
                    onChange={(e) => setConfig({ ...config, autoCloseTimeoutMinutes: parseInt(e.target.value, 10) })}
                    className="flex-1 accent-rose-600 h-2 bg-slate-100 rounded-lg cursor-pointer"
                  />
                  <div className="w-16 px-2 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-center font-mono text-xs font-bold text-slate-700">
                    {config.autoCloseTimeoutMinutes} นาที
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  หากลูกค้านิ่งหรือไม่พิมพ์ตอบกลับภายใน 3 นาที ระบบจะจบการทำงานและเปิดหน้าต่างให้คะแนน 1-5 ดาวอัตโนมัติ
                </p>
              </div>
            </div>

            {/* AI Agent Role & Identity */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center justify-between">
                <span>บทบาทและหน้าที่ของ เจมิไน (System Role & Identity)</span>
                <span className="text-[11px] text-rose-600 font-medium">ก่อนทำงาน AI จะทบทวนบทบาทนี้ตลอดเวลา</span>
              </label>
              <textarea
                rows={3}
                value={config.role}
                onChange={(e) => setConfig({ ...config, role: e.target.value })}
                placeholder="ระบุบทบาทของ AI เช่น คุณคือ AI Agent ชื่อ PINKGIFF..."
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all leading-relaxed"
              />
            </div>

            {/* Tone of Voice */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center justify-between">
                <span>ลักษณะการโต้ตอบและน้ำเสียง (Tone of Voice & Personality)</span>
                <span className="text-[11px] text-slate-500">สไตล์เพื่อนแนะนำเพื่อน ชัดเจนเรื่องราคาและ PV</span>
              </label>
              <textarea
                rows={3}
                value={config.toneOfVoice}
                onChange={(e) => setConfig({ ...config, toneOfVoice: e.target.value })}
                placeholder="ระบุน้ำเสียง เช่น อบอุ่น เป็นกันเอง สุภาพ มีชีวิตชีวา..."
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all leading-relaxed"
              />
            </div>
          </div>

          {/* Section 2: Few-Shot Examples (ตัวอย่างคำถาม ตัวอย่างคำตอบ ตัวอย่างน้ำเสียง) */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-100">
              <div>
                <div className="flex items-center gap-2 text-slate-800 font-bold text-sm">
                  <Award className="w-4 h-4 text-amber-500" />
                  <span>2. คลังตัวอย่างบทสนทนาต้นแบบ (Few-Shot Benchmark Examples)</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  AI จะทบทวนตัวอย่างคำถาม คำตอบ และน้ำเสียงเหล่านี้ก่อนตอบกลับ เพื่อให้ทำงานได้ตรงคำถามและได้ผลลัพธ์เกือบ 100%
                </p>
              </div>

              <button
                type="button"
                onClick={handleAddExample}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 text-xs font-bold transition-all self-start sm:self-auto"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>เพิ่มตัวอย่างการสนทนา</span>
              </button>
            </div>

            {/* Examples List */}
            {(!config.fewShotExamples || config.fewShotExamples.length === 0) ? (
              <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                <p className="text-xs text-slate-500 mb-2">ยังไม่มีตัวอย่างคำถาม-คำตอบต้นแบบ</p>
                <button
                  type="button"
                  onClick={handleAddExample}
                  className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-bold hover:bg-rose-700 transition-colors"
                >
                  + เพิ่มตัวอย่างแรก
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {config.fewShotExamples.map((ex, idx) => (
                  <div 
                    key={ex.id || idx}
                    className="p-4 rounded-xl border border-slate-200 bg-slate-50/60 hover:bg-white hover:border-rose-200 transition-all space-y-3 relative group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="px-2 py-0.5 rounded-md bg-rose-100 text-rose-800 font-mono text-[10px] font-bold">
                        ตัวอย่างที่ {idx + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDeleteExample(ex.id)}
                        className="text-slate-400 hover:text-rose-600 p-1 rounded transition-colors"
                        title="ลบตัวอย่างนี้"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Customer Question */}
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          ❓ ตัวอย่างคำถามจากลูกค้า:
                        </label>
                        <input
                          type="text"
                          value={ex.question}
                          onChange={(e) => handleUpdateExample(ex.id, 'question', e.target.value)}
                          placeholder="เช่น ฟิตต์มีลช่วยเรื่องอะไร มีรสอะไรบ้าง..."
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-800 font-medium focus:outline-none focus:border-rose-500"
                        />
                      </div>

                      {/* Tone & Style Notes */}
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          🎭 คำแนะนำน้ำเสียง/รูปแบบที่ต้องการ:
                        </label>
                        <input
                          type="text"
                          value={ex.toneNotes || ''}
                          onChange={(e) => handleUpdateExample(ex.id, 'toneNotes', e.target.value)}
                          placeholder="เช่น อบอุ่น สุภาพ อธิบายราคาและ PV ชัดเจน"
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-800 font-medium focus:outline-none focus:border-rose-500"
                        />
                      </div>
                    </div>

                    {/* Benchmark Answer */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">
                        💡 ตัวอย่างคำตอบมาตรฐานที่ถูกต้อง (Benchmark Answer):
                      </label>
                      <textarea
                        rows={3}
                        value={ex.answer}
                        onChange={(e) => handleUpdateExample(ex.id, 'answer', e.target.value)}
                        placeholder="ระบุข้อความคำตอบที่ AI ควรตอบกลับอย่างครบถ้วนและไพเราะ..."
                        className="w-full bg-white border border-slate-200 rounded-lg p-2.5 text-xs text-slate-800 font-medium focus:outline-none focus:border-rose-500 leading-relaxed"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Save Action Bar */}
          <div className="sticky bottom-4 z-20 bg-white/95 backdrop-blur-md p-4 rounded-2xl border border-slate-200 shadow-lg flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <ShieldCheck className="w-4 h-4 text-teal-600" />
              <span>การตั้งค่าทั้งหมดจะถูกแคชและส่งต่อไปยัง Gemini ทุกครั้งที่มีการสนทนา</span>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSaveConfig}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs shadow-md transition-all disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <RotateCw className="w-4 h-4 animate-spin" />
                    <span>กำลังบันทึก...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>บันทึกการตั้งค่า AI</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUB-TAB 2: TICKETS & CSAT QUALITY AUDIT */}
      {subTab === 'tickets' && (
        <div className="space-y-5">
          {/* Summary KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                จำนวน Ticket ทั้งหมด
              </div>
              <div className="text-2xl font-black text-slate-800 font-mono">
                {ticketsSummary?.totalCount || tickets.length}
              </div>
              <div className="text-[10px] text-slate-400 mt-1">
                สร้างอัตโนมัติทุกครั้งที่ลูกค้าเริ่มแชต
              </div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                <span>คะแนนความพึงพอใจเฉลี่ย</span>
              </div>
              <div className="text-2xl font-black text-amber-600 font-mono flex items-baseline gap-1">
                <span>{ticketsSummary?.avgRating || 5.0}</span>
                <span className="text-xs text-slate-400 font-normal">/ 5.0</span>
              </div>
              <div className="text-[10px] text-emerald-600 font-bold mt-1">
                CSAT ประเมินจากผู้ใช้งานจริง
              </div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                ยอดประเมิน 5 ดาวเต็ม
              </div>
              <div className="text-2xl font-black text-emerald-600 font-mono">
                {ticketsSummary?.fiveStars || tickets.filter(t => t.rating === 5).length}
              </div>
              <div className="text-[10px] text-slate-400 mt-1">
                ลูกค้าพึงพอใจในระดับยอดเยี่ยม
              </div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-rose-500" />
                <span>จบอัตโนมัติ (3 นาที)</span>
              </div>
              <div className="text-2xl font-black text-rose-600 font-mono">
                {ticketsSummary?.timeoutCount || tickets.filter(t => t.status === 'timeout').length}
              </div>
              <div className="text-[10px] text-slate-400 mt-1">
                ปิดเซสชันเมื่อไม่มีการตอบกลับ
              </div>
            </div>
          </div>

          {/* Search & Filter Toolbar */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="ค้นหา Ticket ID, ชื่อลูกค้า, เบอร์โทร..."
                value={ticketSearch}
                onChange={(e) => setTicketSearch(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-rose-500"
              />
            </div>

            <div className="flex items-center gap-1.5 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
              <button
                onClick={() => setRatingFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors ${
                  ratingFilter === 'all'
                    ? 'bg-slate-800 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                ทั้งหมด ({tickets.length})
              </button>
              <button
                onClick={() => setRatingFilter('5')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors ${
                  ratingFilter === '5'
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                ⭐ 5 ดาว
              </button>
              <button
                onClick={() => setRatingFilter('4')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors ${
                  ratingFilter === '4'
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                ⭐ 4 ดาว
              </button>
              <button
                onClick={() => setRatingFilter('low')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors ${
                  ratingFilter === 'low'
                    ? 'bg-rose-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                ⚠️ 1-3 ดาว (ปรับปรุง)
              </button>
              <button
                onClick={() => setRatingFilter('timeout')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors ${
                  ratingFilter === 'timeout'
                    ? 'bg-zinc-700 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                ⏰ หมดเวลา 3 นาที
              </button>
            </div>
          </div>

          {/* Tickets List */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            {filteredTickets.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40 text-slate-400" />
                <p className="text-xs font-medium">ไม่พบประวัติ Ticket ตามเงื่อนไขการค้นหา</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredTickets.map((t) => (
                  <div 
                    key={t.id || t.ticketId}
                    className="p-4 hover:bg-slate-50/80 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-3"
                  >
                    {/* Ticket Header & Customer */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                          #{t.ticketId}
                        </span>
                        <span className="text-xs font-bold text-slate-800">
                          {t.customerName || 'คุณลูกค้า (Guest)'}
                        </span>
                        {t.memberId && (
                          <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                            ID: {t.memberId}
                          </span>
                        )}
                        <span className="text-[11px] text-slate-400 ml-1">
                          • {t.createdAt}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        {t.phone && <span>📞 {t.phone}</span>}
                        <span>💬 {t.messagesCount || t.messages?.length || 0} ข้อความ</span>
                        {t.cartTotal ? (
                          <span className="font-bold text-teal-700">
                            🛒 ยอดสั่งซื้อ: ฿{t.cartTotal.toLocaleString()}
                          </span>
                        ) : null}
                      </div>

                      {/* Feedback Comment */}
                      {t.feedback && (
                        <div className="text-xs text-slate-600 bg-amber-50/70 border border-amber-200/60 rounded-lg px-2.5 py-1.5 mt-1">
                          <span className="font-bold text-amber-900">ความคิดเห็นลูกค้า:</span> "{t.feedback}"
                        </div>
                      )}
                    </div>

                    {/* Status, Stars & View Transcript Button */}
                    <div className="flex items-center gap-3 shrink-0 self-end md:self-auto">
                      {/* Rating Stars */}
                      {typeof t.rating === 'number' && t.rating > 0 ? (
                        <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-lg">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-3.5 h-3.5 ${
                                  star <= (t.rating || 0)
                                    ? 'text-amber-500 fill-amber-500'
                                    : 'text-slate-200'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-xs font-bold text-amber-800 ml-1">
                            {t.rating}.0
                          </span>
                        </div>
                      ) : (
                        <span className="text-[11px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                          ยังไม่ประเมิน
                        </span>
                      )}

                      {/* Status Badge */}
                      <span className={`px-2 py-1 rounded-lg text-[10px] font-bold ${
                        t.status === 'timeout'
                          ? 'bg-zinc-100 text-zinc-700 border border-zinc-200'
                          : 'bg-teal-50 text-teal-700 border border-teal-200'
                      }`}>
                        {t.status === 'timeout' ? '⏰ จบอัตโนมัติ (3 นาที)' : '✅ สิ้นสุดการสนทนา'}
                      </span>

                      {/* Transcript Button */}
                      <button
                        onClick={() => setSelectedTicketForTranscript(t)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 text-white hover:bg-slate-900 text-xs font-bold transition-colors shadow-2xs"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>ดูบทสนทนา</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SUB-TAB 3: SIMULATOR (Chat & Shop เสมือน - ดึงโครงสร้างหน้าจริง https://GIFFFORYOU.COM/chatandshop) */}
      {subTab === 'simulator' && (
        <div className="space-y-4 animate-fade-in">
          {/* Top Control Bar for Simulator */}
          <div className="bg-gradient-to-r from-teal-950 via-slate-900 to-zinc-900 p-4 rounded-2xl text-white border border-teal-800/40 shadow-md flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-500/20 border border-teal-400/40 flex items-center justify-center text-teal-300">
                <Smartphone className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-black text-white">
                    Chat & Shop เสมือน (Live Simulator Environment)
                  </h3>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-500/20 text-teal-300 border border-teal-500/40">
                    หน้าจอจริง https://GIFFFORYOU.COM/chatandshop
                  </span>
                </div>
                <p className="text-[11px] text-zinc-300">
                  ทดสอบการสนทนาและปิดการขายตามแพทเทิร์นจริง เพื่อให้คำถาม-คำตอบเป็นไปในทิศทางเดียวกันและลดความผิดพลาดเป็น 0
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setSimCart([]);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-xs text-zinc-200 border border-zinc-700 font-bold transition-all shadow-2xs"
                title="ล้างตะกร้าสินค้าจำลอง"
              >
                <ShoppingBag className="w-3.5 h-3.5 text-rose-400" />
                <span>ล้างตะกร้าจำลอง ({simCartCount})</span>
              </button>
              <button
                onClick={() => {
                  fetchData();
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-600 hover:bg-teal-500 text-xs text-white font-bold transition-all shadow-sm"
                title="รีเฟรชการเชื่อมต่อ"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>รีโหลดค่าโมเดล</span>
              </button>
            </div>
          </div>

          {/* Customer Context Simulator Bar */}
          <div className="bg-white rounded-xl p-3 border border-slate-200 shadow-2xs flex flex-wrap items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-2 text-slate-700 font-semibold">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span>สถานะสมาชิกทดสอบ:</span>
              <span className="text-rose-600 font-bold">{simCustomer.name}</span>
              <span className="text-slate-400">|</span>
              <span>รหัส Giffarine ID:</span>
              <span className="font-mono font-bold text-slate-800">{simCustomer.memberId}</span>
              <span className="text-slate-400">|</span>
              <span>โทร:</span>
              <span className="font-mono text-slate-800">{simCustomer.phone}</span>
            </div>

            <div className="text-[11px] text-slate-500 flex items-center gap-1">
              <span>ที่อยู่จำลอง:</span>
              <span className="truncate max-w-xs text-slate-700 font-medium" title={simCustomer.address}>
                {simCustomer.address}
              </span>
            </div>
          </div>

          {/* Embedded Real Chat & Shop Interface */}
          <div className="bg-slate-100 rounded-2xl border border-slate-300/80 shadow-lg overflow-hidden min-h-[750px] relative">
            <ChatAndShopView
              onBackToStore={() => setSubTab('config')}
              onAddToCart={(product, qty) => handleSimAddToCart(product, qty || 1)}
              onOpenCart={() => {}}
              cartCount={simCartCount}
              cartItems={simCart}
              cartTotal={simCartTotal}
              customerUser={simCustomer}
              onOpenCheckout={() => {
                alert(`[Chat & Shop เสมือน] นำส่งคำสั่งซื้อจำลองเรียบร้อยแล้ว!\nยอดรวม: ฿${simCartTotal.toLocaleString()} บาท\nจัดส่งให้: ${simCustomer.name} (${simCustomer.phone})`);
              }}
              onProceedToCheckout={() => {
                alert(`[Chat & Shop เสมือน] สรุปยอดและเข้าสู่ขั้นตอนการชำระเงินสำเร็จ!`);
              }}
            />
          </div>
        </div>
      )}

      {/* TRANSCRIPT MODAL */}
      {selectedTicketForTranscript && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden border border-slate-200">
            {/* Modal Header */}
            <div className="p-4 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-rose-400" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-extrabold text-sm tracking-tight">
                      บทสนทนาย้อนหลัง Ticket #{selectedTicketForTranscript.ticketId}
                    </span>
                    {typeof selectedTicketForTranscript.rating === 'number' && (
                      <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px] font-bold border border-amber-500/40">
                        ⭐ {selectedTicketForTranscript.rating} ดาว
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-300">
                    ลูกค้า: {selectedTicketForTranscript.customerName || 'คุณลูกค้า'} | เริ่ม: {selectedTicketForTranscript.createdAt}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedTicketForTranscript(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Transcript Messages Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
              {(!selectedTicketForTranscript.messages || selectedTicketForTranscript.messages.length === 0) ? (
                <p className="text-center text-xs text-slate-400 py-8">ไม่มีข้อมูลข้อความใน Ticket นี้</p>
              ) : (
                selectedTicketForTranscript.messages.map((m, idx) => (
                  <div
                    key={m.id || idx}
                    className={`flex flex-col ${
                      m.sender === 'user' ? 'items-end' : 'items-start'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-1 text-[10px] text-slate-400">
                      <span>{m.sender === 'user' ? (selectedTicketForTranscript.customerName || 'ลูกค้า') : 'PINKGIFF AI'}</span>
                      <span>• {m.timestamp}</span>
                    </div>
                    <div
                      className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed ${
                        m.sender === 'user'
                          ? 'bg-rose-600 text-white rounded-tr-none'
                          : 'bg-white text-slate-800 border border-slate-200 rounded-tl-none shadow-2xs'
                      }`}
                    >
                      <p className="whitespace-pre-line">{m.text}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Customer Rating and Feedback Bar */}
            {selectedTicketForTranscript.feedback && (
              <div className="p-3 bg-amber-50 border-t border-amber-200 text-xs text-amber-900">
                <span className="font-bold">ความคิดเห็นลูกค้า:</span> "{selectedTicketForTranscript.feedback}"
              </div>
            )}

            {/* Modal Footer */}
            <div className="p-3.5 bg-slate-100 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setSelectedTicketForTranscript(null)}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold transition-colors"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
